import type {
  DataSyncLogQueryParams,
  FetchDataSyncLogsParams,
  PaginatedDataSyncTaskLogResponse,
} from './validations/types';

import {
  keepPreviousData,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { vestigasApi } from '~/services/kyClient';

import { toSnakeCase, withoutObjectKeysWhereValueIs } from '@vestigas/utils';

import { queryKeysDataSyncJob } from './queryKeys';

const defaultQueryParams: DataSyncLogQueryParams = {
  limit: 50,
  offset: 0,
  orderBy: 'finished_on',
  sortOrder: 'DESC',
};

/**
 * Fetches paginated data sync task logs.
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Promise containing paginated log data
 */
export const fetchDataSyncLogs = async (
  params?: FetchDataSyncLogsParams,
): Promise<PaginatedDataSyncTaskLogResponse> => {
  const mergedParams = {
    ...defaultQueryParams,
    ...params,
  };

  const searchParams = withoutObjectKeysWhereValueIs(
    {
      dataSyncTaskId: mergedParams.dataSyncTaskId,
      limit: mergedParams.limit,
      offset: mergedParams.offset,
      orderBy: mergedParams.orderBy
        ? toSnakeCase(mergedParams.orderBy)
        : undefined,
      sortOrder: mergedParams.sortOrder,
    },
    [undefined],
  );

  const response = await vestigasApi
    .get('data_sync/log', {
      searchParams,
    })
    .json<PaginatedDataSyncTaskLogResponse>();

  return response;
};

/**
 * React Query hook for fetching paginated data sync task logs.
 *
 * @param params - Query parameters for filtering and pagination
 * @param options - Additional React Query options
 * @returns Query result containing paginated list of data sync logs
 */
export const useQueryDataSyncLogs = (
  params?: DataSyncLogQueryParams,
  options?: Omit<
    UseQueryOptions<PaginatedDataSyncTaskLogResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  const queryKey = queryKeysDataSyncJob.getAllLogs(params);

  return useQuery({
    placeholderData: keepPreviousData,
    queryFn: async () => fetchDataSyncLogs(params),
    queryKey,
    staleTime: ms('5m'),
    ...options,
  });
};
